import React, { useMemo } from 'react';
import { Box, Button, CircularProgress, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { IMedispanStagingDiff } from 'models/application-manager/medispan/IMedispanStagingDiff';
import { IMedispanStagingReview } from 'models/application-manager/medispan/IMedispanStagingReview';
import { IMedispanApprovedDiff } from 'models/application-manager/medispan/IMedispanApprovedDiff';
import { MedispanStagingClient } from 'clients/medispan-staging-client';
import { DownloadFileUtils } from 'utils/download-file-utils';
import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import { buildQaId } from 'utils/build-qa-id';
import { SectionTitleStyled } from '../components/section-title';
import { MedispanStagingTableStyled } from './medispan-staging-table';
import { MedispanReviewedTableStyled } from './reviewed-table';
import { RejectionReasonModalStyled } from './rejection-reason-modal';

const getQaId = buildQaId('application-manager.medispan-staging', '.');

type ActiveTab = 'need-review' | 'reviewed';

const exportToCsv = (
  rows: IMedispanStagingDiff[] | IMedispanApprovedDiff[],
  tab: ActiveTab,
): void => {
  let csvContent = '';

  if (tab === 'need-review') {
    const diffRows = rows as IMedispanStagingDiff[];
    csvContent =
      'Drug Descriptor ID,GPI Current,GPI Staged,GPI 10 Current,GPI 10 Staged,Drug Name Current,Drug Name Staged\n';
    csvContent += diffRows
      .map(r =>
        [
          r.drugDescriptorId,
          r.current?.gpi ?? '',
          r.staged?.gpi ?? '',
          r.current?.gpi10 ?? '',
          r.staged?.gpi10 ?? '',
          `"${r.current?.drugName ?? ''}"`,
          `"${r.staged?.drugName ?? ''}"`,
        ].join(','),
      )
      .join('\n');
  } else {
    const approvedRows = rows as IMedispanApprovedDiff[];
    csvContent =
      'Drug Descriptor ID,GPI Current,GPI Staged,GPI 10 Current,GPI 10 Staged,Drug Name Current,Drug Name Staged,User\n';
    csvContent += approvedRows
      .map(r =>
        [
          r.drug_descriptor_id,
          r.gpi_current ?? '',
          r.gpi_staged ?? '',
          r.gpi10_current ?? '',
          r.gpi10_staged ?? '',
          `"${r.drug_name_current ?? ''}"`,
          `"${r.drug_name_staged ?? ''}"`,
          `"${r.approver_display_name}"`,
        ].join(','),
      )
      .join('\n');
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  DownloadFileUtils.downloadUrl(url, `medispan-staging-${tab}.csv`);
};

const MedispanStaging = (): JSX.Element => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = React.useState<ActiveTab>('need-review');
  const [diff, setDiff] = React.useState<IMedispanStagingDiff[]>([]);
  const [review, setReview] = React.useState<IMedispanStagingReview | null>(null);
  const [reviewedDiffs, setReviewedDiffs] = React.useState<IMedispanApprovedDiff[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [actionLoading, setActionLoading] = React.useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = React.useState<boolean>(false);

  const canApprove = UserUtils.userIsPermitted(UserPermissions.MedispanStagingApprove);
  const canExecute = UserUtils.userIsPermitted(UserPermissions.MedispanStagingExecute);

  const loadData = async () => {
    setLoading(true);
    try {
      const [diffRes, statusRes, reviewedRes] = await Promise.all([
        MedispanStagingClient.getStagingDiff(),
        MedispanStagingClient.getStatus(),
        MedispanStagingClient.getReviewedDiffs(),
      ]);
      setDiff(diffRes.data.diffs);
      setReview(statusRes.data);
      setReviewedDiffs(reviewedRes.data);
    } catch {
      dispatch(notifyError('Failed to load Medispan staging data.'));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await MedispanStagingClient.approve();
      dispatch(notifySuccess('Medispan staging changes approved.'));
      await loadData();
    } catch {
      dispatch(notifyError('Failed to approve Medispan staging changes.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    setShowRejectModal(false);
    setActionLoading(true);
    try {
      await MedispanStagingClient.reject(reason);
      dispatch(notifySuccess('Medispan staging changes rejected.'));
      await loadData();
    } catch {
      dispatch(notifyError('Failed to reject Medispan staging changes.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExecute = async () => {
    setActionLoading(true);
    try {
      await MedispanStagingClient.executeUpdate();
      dispatch(notifySuccess('Medispan update executed successfully.'));
      await loadData();
    } catch {
      dispatch(notifyError('Failed to execute Medispan update.'));
    } finally {
      setActionLoading(false);
    }
  };

  const isPending = review?.status === 'pending';
  const isApproved = review?.status === 'approved';
  const exportRows = activeTab === 'need-review' ? diff : reviewedDiffs;
  const rightSideItems = useMemo(
    () => [
      {
        key: 'export',
        element: (
          <Button
            variant="outlined"
            size="small"
            onClick={() => exportToCsv(exportRows, activeTab)}
            disabled={exportRows.length === 0}
            data-qa-id={getQaId('export-xls-button')}
          >
            EXPORT CSV
          </Button>
        ),
      },
    ],
    [exportRows, activeTab],
  );

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: 8 }}
      >
        <SectionTitleStyled title="Medispan Staging Report" rightSideItems={rightSideItems} />
      </Grid>

      <Tabs
        value={activeTab}
        onChange={(_e, val: ActiveTab) => setActiveTab(val)}
        indicatorColor="primary"
        textColor="primary"
        style={{ marginBottom: 16, borderBottom: '1px solid #e0e0e0' }}
      >
        <Tab label="NEED REVIEW" value="need-review" data-qa-id={getQaId('tab-need-review')} />
        <Tab label="REVIEWED" value="reviewed" data-qa-id={getQaId('tab-reviewed')} />
      </Tabs>

      {loading ? (
        <Grid container justifyContent="center" style={{ padding: 32 }}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {activeTab === 'need-review' && (
            <>
              {isPending && diff.length > 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    backgroundColor: '#FFF8E1',
                    borderRadius: 4,
                    paddingTop: 12,
                    paddingBottom: 12,
                    paddingRight: 16,
                    paddingLeft: 16,
                    marginBottom: 16,
                  }}
                  data-qa-id={getQaId('review-banner')}
                >
                  <Typography style={{ color: '#5D4037', fontSize: 12 }}>
                    {diff.length} staged {diff.length === 1 ? 'item' : 'items'} need review
                  </Typography>
                  {canApprove && (
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="error"
                        style={{ boxShadow: 'none' }}
                        disabled={actionLoading}
                        onClick={() => setShowRejectModal(true)}
                        data-qa-id={getQaId('reject-all-button')}
                      >
                        REJECT ALL
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ boxShadow: 'none' }}
                        disabled={actionLoading}
                        onClick={handleApprove}
                        data-qa-id={getQaId('approve-all-button')}
                      >
                        {actionLoading ? <CircularProgress size={16} /> : 'APPROVE ALL'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {canExecute && isApproved && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    backgroundColor: '#E8F5E9',
                    borderRadius: 4,
                    paddingTop: 12,
                    paddingBottom: 12,
                    paddingRight: 16,
                    paddingLeft: 16,
                    marginBottom: 16,
                  }}
                  data-qa-id={getQaId('review-banner')}
                >
                  <Typography style={{ color: '#2E7D32', fontSize: 12 }}>
                    {diff.length} staged {diff.length === 1 ? 'item' : 'items'} are marked as
                    approved
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      color="secondary"
                      style={{ boxShadow: 'none' }}
                      disabled={actionLoading}
                      onClick={handleExecute}
                      data-qa-id={getQaId('execute-button')}
                    >
                      {actionLoading ? <CircularProgress size={20} /> : 'Execute Update'}
                    </Button>
                  </Box>
                </Box>
              )}

              <MedispanStagingTableStyled rows={diff} />
            </>
          )}

          {activeTab === 'reviewed' && <MedispanReviewedTableStyled rows={reviewedDiffs} />}
        </>
      )}

      <RejectionReasonModalStyled
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onSubmit={handleReject}
      />
    </>
  );
};

export default MedispanStaging;
